import api from "./api";

function getApiErrorMessage(dataOrError, fallback) {
  const data = dataOrError?.response?.data ?? dataOrError;
  return (
    data?.getMessageText?.join(", ") ||
    data?.message?.[0]?.messageText ||
    dataOrError?.message ||
    fallback
  );
}

/**
 * Login with username and password
 * @param {Object} credentials - { username, password }
 * @returns {Promise<Object>} User data
 */
export async function login({ username, password }) {
  try {
    const { data } = await api.post("/trading/auth/login", { username, password });

    if (!data.isSuccessful) {
      throw new Error(getApiErrorMessage(data, "ورود ناموفق بود"));
    }

    return data.data;
  } catch (error) {
    if (error.response) {
      throw new Error(getApiErrorMessage(error, "ورود ناموفق بود"));
    }
    throw error;
  }
}

/**
 * Register trader with mobile (no OTP; status becomes Waiting until admin confirms)
 * @param {Object} params - { fullName, phoneNumber, provinceId, cityId, address }
 * @returns {Promise<Object>} { personId, tradingPersonId, userId } — no JWT
 */
export async function mobileRegister({ fullName, phoneNumber, provinceId, cityId, address }) {
  try {
    const { data } = await api.post("/trading/auth/mobileregister", {
      fullName,
      phoneNumber,
      provinceId,
      cityId,
      address,
    });

    if (!data.isSuccessful) {
      throw new Error(getApiErrorMessage(data, "ثبت نام ناموفق بود"));
    }

    return data.data;
  } catch (error) {
    if (error.response) {
      throw new Error(getApiErrorMessage(error, "ثبت نام ناموفق بود"));
    }
    throw error;
  }
}

/**
 * Login with mobile number (sends OTP)
 * @param {Object} params - { phoneNumber }
 * @returns {Promise<Object>} { tradingPersonId, verificationToken, expireTime }
 */
export async function mobileLogin({ phoneNumber }) {
  try {
    const { data } = await api.post("/trading/auth/mobilelogin", { phoneNumber });

    if (!data.isSuccessful) {
      throw new Error(getApiErrorMessage(data, "ارسال کد تایید ناموفق بود"));
    }

    return data.data;
  } catch (error) {
    if (error.response) {
      throw new Error(getApiErrorMessage(error, "ارسال کد تایید ناموفق بود"));
    }
    throw error;
  }
}

/**
 * Verify login with token and OTP code
 * @param {Object} params - { token, code }
 * @returns {Promise<Object>} Login response with JWT
 */
export async function verifyLogin({ token, code }) {
  try {
    const { data } = await api.post("/trading/auth/verifylogin", { token, code });

    if (!data.isSuccessful) {
      throw new Error(getApiErrorMessage(data, "تایید کد ناموفق بود"));
    }

    return data.data;
  } catch (error) {
    if (error.response) {
      throw new Error(getApiErrorMessage(error, "تایید کد ناموفق بود"));
    }
    throw error;
  }
}

/**
 * Verify register with token and code (OTP verification for registration)
 * @param {Object} params - { token, code }
 * @returns {Promise<Object>} Response data (contains verificationToken, status, roleType, expireTime)
 */
export async function verifyRegister({ token, code }) {
  const { data } = await api.post("/trading/auth/verifyregister", { token, code });
  
  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "تایید ثبت نام ناموفق بود";
    throw new Error(errorMessage);
  }
  
  return data.data;
}

export async function me() {
  const { data } = await api.get("/auth/me");
  return data.user;
}

export async function logout() {
  await api.post("/auth/logout");
}

export async function refresh() {
  await api.post("/auth/refresh");
}

export async function setPassword({ password, confirmPassword, name, city, address, description, businessLicenseImage }) {
  const { data } = await api.post("/auth/set-password", { password, confirmPassword, name, city, address, description, businessLicenseImage });
  
  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "تنظیم رمز عبور ناموفق بود";
    throw new Error(errorMessage);
  }
  
  return data.data || data;
}

/**
 * Admin resets a user's password
 */
export async function adminResetPassword(userId, newPassword) {
  try {
    const { data } = await api.put(`/auth/admin/reset-password/${userId}`, { newPassword });
    return data;
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'تغییر رمز عبور ناموفق بود';
    throw new Error(message);
  }
}

/**
 * User changes their own password
 */
export async function changePassword(currentPassword, newPassword) {
  try {
    const { data } = await api.put("/auth/change-password", { currentPassword, newPassword });
    return data;
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'تغییر رمز عبور ناموفق بود';
    throw new Error(message);
  }
}


